/**
 * GustoPass till scanner (staff PWA). Served by the API Worker through Workers Static Assets behind
 * Cloudflare Access, so `/v1/*` is same-origin; `VITE_API_BASE` points elsewhere in development.
 */
import "./styles.css";
import { h } from "./dom-helpers";
import type { RenderedScreen, ScannerApp, Screen } from "./scanner-app-context";
import { renderBill } from "./screens/bill-entry-screen";
import { renderScan } from "./screens/member-scan-screen";
import { renderSignIn } from "./screens/staff-sign-in-screen";
import { renderTillPicker } from "./screens/till-picker-screen";
import { renderResult } from "./screens/transaction-result-screen";
import { ApiHttpError, getDevToken, setDevToken, staffApi } from "./staff-api-client";
import { strings } from "./strings";
import { clearTill, loadTill } from "./till-selection-store";
import { flushQueue } from "./transaction-submitter";
import { listQueued, onQueueChange, removeQueued } from "./transaction-retry-queue";

const QUEUE_RETRY_MS = 30_000;
const root = document.querySelector<HTMLElement>("#app");
if (!root) throw new Error("#app missing");

/** Without Access in front (local dev, preview), staff must paste the dev bypass token. */
const needsDevToken = import.meta.env.DEV || ["localhost", "127.0.0.1"].includes(location.hostname);

const header = h("header", { className: "topbar" });
const queuePanel = h("section", { className: "card stack queue", hidden: true });
const main = h("main", { className: "content" });
root.replaceChildren(header, queuePanel, main);

let current: RenderedScreen | null = null;

const app: ScannerApp = {
  till: loadTill(),
  brands: [],
  malls: [],
  navigate(screen: Screen) {
    current?.dispose?.();
    current = render(screen);
    main.replaceChildren(current.el);
    window.scrollTo(0, 0);
    app.refreshHeader();
  },
  handleAuthError(error) {
    if (error instanceof ApiHttpError && (error.status === 401 || error.status === 403)) {
      app.navigate({ name: "signin", expired: true });
      return true;
    }
    return false;
  },
  refreshHeader() {
    void renderHeader();
  },
};

function render(screen: Screen): RenderedScreen {
  switch (screen.name) {
    case "signin":
      return renderSignIn(app, screen.expired ?? false);
    case "till":
      return renderTillPicker(app);
    case "scan":
      return app.till ? renderScan(app) : renderTillPicker(app);
    case "bill":
      return renderBill(app, screen.member);
    case "result":
      return renderResult(app, screen.result, screen.queued);
  }
}

async function renderHeader(): Promise<void> {
  const till = app.till;
  const mall = app.malls.find((m) => m.id === till?.mallId);
  const brand = app.brands.find((b) => b.id === till?.brandId);
  let queued: Awaited<ReturnType<typeof listQueued>> = [];
  try {
    queued = await listQueued();
  } catch (error) {
    console.error("retry queue unavailable", error);
  }
  const pending = queued.filter((q) => q.status === "pending").length;

  const items: (HTMLElement | null)[] = [
    h("h1", {}, strings.appTitle),
    till ? h("span", { className: "till-label" }, [brand?.name ?? till.brandId, mall?.name ?? till.mallId].join(" · ")) : null,
    navigator.onLine ? null : h("span", { className: "badge warn" }, strings.offline),
    queued.length > 0
      ? h(
          "button",
          { type: "button", className: "badge", "data-testid": "queue-badge", onclick: () => (queuePanel.hidden = !queuePanel.hidden) },
          strings.queueBadge(pending),
        )
      : null,
    till ? h("button", { type: "button", className: "ghost small", onclick: () => { clearTill(); app.till = null; app.navigate({ name: "till" }); } }, strings.tillChange) : null,
    getDevToken() ? h("button", { type: "button", className: "ghost small", onclick: () => { setDevToken(null); app.navigate({ name: "signin" }); } }, strings.signOut) : null,
  ];
  header.replaceChildren(...items.filter((item): item is HTMLElement => item !== null));

  queuePanel.replaceChildren(
    h("h2", {}, strings.queueTitle),
    ...queued.map((q) =>
      h(
        "div",
        { className: "row queue-item" },
        h("span", {}, `${q.request.receiptNo} · ${strings.czk(q.request.totalCzk)}`),
        q.status === "failed" ? h("span", { className: "error" }, `${strings.queueFailed} (${q.lastError ?? ""})`) : null,
        q.status === "failed" ? h("button", { type: "button", className: "ghost small", onclick: () => void removeQueued(q.idempotencyKey) }, strings.queueRemove) : null,
      ),
    ),
    h("button", { type: "button", className: "secondary", onclick: () => void flushQueue() }, strings.queueSendNow),
  );
  if (queued.length === 0) queuePanel.hidden = true;
}

function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.register("/sw.js").catch((error: unknown) => console.warn("service worker registration failed", error));
}

async function start(): Promise<void> {
  registerServiceWorker();
  onQueueChange(() => app.refreshHeader());
  window.addEventListener("online", () => {
    app.refreshHeader();
    void flushQueue();
  });
  window.addEventListener("offline", () => app.refreshHeader());
  window.setInterval(() => void flushQueue(), QUEUE_RETRY_MS);

  main.replaceChildren(h("p", { className: "muted" }, strings.loading));
  try {
    [app.brands, app.malls] = await Promise.all([staffApi.listBrands(), staffApi.listMalls()]);
  } catch (error) {
    console.error("content load failed", error);
  }
  if (needsDevToken && !getDevToken()) app.navigate({ name: "signin" });
  else app.navigate(app.till ? { name: "scan" } : { name: "till" });
  void flushQueue();
}

void start();
